import { debounce } from "lodash";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { appMessage } from "../../helpers/message";

export interface AppContextState {
  volume: number; // 0 to 100 (0% to 100%)
  setVolume: (volume: number) => void;
  setVolumeSliderFocused: (value: boolean) => void;
}

const AppContext = React.createContext<AppContextState | undefined>(undefined);

export interface AppContextProviderProps {
  children: React.ReactNode;
}

export const useAppContext: () => AppContextState = () => {
  const context = useContext(AppContext);
  if (!context) {
      throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
};

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [volume, setVolume] = useState(-1); // -1 by default, means not supported

  const volumeSliderFocusedRef = useRef(false);
  const postVolumeRef = useRef({
    setVolume: debounce((v) => {
      appMessage.postVolume(v);
    }, 100, { leading: true, maxWait: 100 }),
  });

  useEffect(() => {
    const volumeListener = (volume: number) => {
      if (volumeSliderFocusedRef.current) return; // no update if slider is focused
      setVolume(volume);
    };
    appMessage.addEventListener("volume", volumeListener);
    return () => {
      appMessage.removeEventListener("volume", volumeListener);
    };
  }, []);

  const state = useMemo<AppContextState>(() => ({
    volume,
    setVolume: (v) => {
      postVolumeRef.current.setVolume(v);
      setVolume(v);
    },
    setVolumeSliderFocused: (value) => volumeSliderFocusedRef.current = value,
  }), [volume]);

  return (
    <AppContext.Provider value={state}>
      {children}
    </AppContext.Provider>
  );
};